import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { GroupedSelect } from '@components/atoms/ReactSelect/GroupedSelect';
import { SingleValue, MultiValue } from 'react-select';
import {
  MaterialCatalog,
  MaterialCatalogCreateInput,
  MaterialCatalogUpdateInput,
  MaterialCategory,
  CategoryType,
  Warehouse,
} from '@models/warehouse.model';
import {
  ImperialWeightOptions,
  MetricWeightOptions,
  WeightOptions,
} from '@utils/options';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StraightenIcon from '@mui/icons-material/Straighten';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import InventoryIcon from '@mui/icons-material/Inventory';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTranslation } from '@hooks/useTranslation';

export interface InitialStockData {
  quantity: number;
  unitCost: number;
  warehouseId: string;
}

interface MaterialCatalogFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: MaterialCatalogCreateInput | MaterialCatalogUpdateInput,
    initialStock?: InitialStockData,
  ) => Promise<void>;
  material?: MaterialCatalog | null;
  categories: MaterialCategory[];
  warehouses?: Warehouse[];
  existingMaterials?: MaterialCatalog[];
  isLoading?: boolean;
}

// Convert shared unit options to ReactSelect format (same pattern as CreateEditMaterialModal)
const convertOptions = (options: { id: string; name: string; group?: string }[]) =>
  options.map((opt) => ({ value: opt.id, label: opt.name, group: opt.group }));

const weightOptions = convertOptions(WeightOptions);
const metricWeightOptions = convertOptions(MetricWeightOptions);
const imperialWeightOptions = convertOptions(ImperialWeightOptions);

/**
 * Generate a unique SKU from a material name and category.
 * Format: CAT-NAME-001
 *   CAT  = first 3 letters of category (or "MAT" if none)
 *   NAME = first 3 consonants/letters of material name
 *   001  = sequential number based on existing SKUs with same prefix
 */
const generateSku = (
  materialName: string,
  categoryName: string | undefined,
  existingSkus: string[],
): string => {
  const sanitize = (str: string) =>
    str
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');

  // Category prefix: first 3 chars
  const catPrefix = categoryName
    ? sanitize(categoryName).slice(0, 3).padEnd(3, 'X')
    : 'MAT';

  // Name part: first 3 meaningful chars
  const nameChars = sanitize(materialName);
  const namePart = nameChars.slice(0, 3).padEnd(3, 'X');

  const prefix = `${catPrefix}-${namePart}`;

  // Find the next sequential number for this prefix
  const existingNumbers = existingSkus
    .filter((s) => s.toUpperCase().startsWith(prefix))
    .map((s) => {
      const match = s.match(/(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    });

  const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

  return `${prefix}-${String(nextNumber).padStart(3, '0')}`;
};

export const MaterialCatalogFormModal: React.FC<MaterialCatalogFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  material,
  categories,
  warehouses = [],
  existingMaterials = [],
  isLoading = false,
}) => {
  const tWarehouse = useTranslation('warehouse');
  const tCommon = useTranslation('common');
  const isEditing = !!material;

  // Form state
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [skuManuallyEdited, setSkuManuallyEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [defaultSupplier, setDefaultSupplier] = useState('');
  const [unit, setUnit] = useState('');
  const [unitSystem, setUnitSystem] = useState<'metric' | 'imperial'>('imperial');
  const [averageCost, setAverageCost] = useState<string>('');
  const [defaultPricePerUnit, setDefaultPricePerUnit] = useState<string>('');
  const [reorderPoint, setReorderPoint] = useState<string>('');
  const [suggestedOrderQuantity, setSuggestedOrderQuantity] = useState<string>('');
  const [leadTimeDays, setLeadTimeDays] = useState<string>('');
  const [isActive, setIsActive] = useState(true);

  // Initial stock (only for new materials)
  const [initialQuantity, setInitialQuantity] = useState<string>('');
  const [initialCost, setInitialCost] = useState<string>('');
  const [warehouseId, setWarehouseId] = useState('');

  // Filter categories to only show MATERIAL or BOTH types
  const materialCategories = useMemo(() => {
    return categories.filter(
      (c) => c.type === CategoryType.MATERIAL || c.type === CategoryType.BOTH
    );
  }, [categories]);

  // ReactSelect options
  const categoryOptions: SelectOption[] = useMemo(
    () => materialCategories.map((cat) => ({ value: cat.categoryId, label: cat.name })),
    [materialCategories],
  );

  const warehouseOptions: SelectOption[] = useMemo(
    () =>
      warehouses.map((wh) => ({
        value: wh.warehouseId,
        label: `${wh.name}${wh.isDefault ? ' (Default)' : ''}`,
      })),
    [warehouses],
  );

  // Initial stock only makes sense once a warehouse is named — otherwise the
  // quantity would silently land in whichever warehouse happened to be default.
  const wantsInitialStock = !isEditing && parseFloat(initialQuantity) > 0;
  const needsWarehouseForInitialStock = wantsInitialStock && !warehouseId;

  // Existing SKUs for uniqueness check
  const existingSkus = useMemo(
    () => existingMaterials.map((m) => m.sku || '').filter(Boolean),
    [existingMaterials],
  );

  // Selected category name (for SKU generation)
  const selectedCategoryName = useMemo(
    () => materialCategories.find((c) => c.categoryId === categoryId)?.name,
    [materialCategories, categoryId],
  );

  // Auto-generate SKU when name or category changes (only if not manually edited)
  useEffect(() => {
    if (!isEditing && !skuManuallyEdited && name.trim().length >= 2) {
      const generated = generateSku(name.trim(), selectedCategoryName, existingSkus);
      setSku(generated);
    }
  }, [name, selectedCategoryName, existingSkus, isEditing, skuManuallyEdited]);

  const handleSkuChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSku(e.target.value.toUpperCase());
    setSkuManuallyEdited(true);
  }, []);

  const handleRegenerateSku = useCallback(() => {
    if (name.trim().length >= 2) {
      const generated = generateSku(name.trim(), selectedCategoryName, existingSkus);
      setSku(generated);
      setSkuManuallyEdited(false);
    }
  }, [name, selectedCategoryName, existingSkus]);

  // Calculate margin
  const margin = useMemo(() => {
    const cost = parseFloat(averageCost) || 0;
    const price = parseFloat(defaultPricePerUnit) || 0;
    if (cost > 0 && price > 0) {
      const marginPercent = ((price - cost) / cost) * 100;
      return marginPercent.toFixed(1);
    }
    return null;
  }, [averageCost, defaultPricePerUnit]);

  // Reset form when modal opens/closes or material changes
  useEffect(() => {
    if (isOpen) {
      if (material) {
        setName(material.name || '');
        setSku(material.sku || '');
        setSkuManuallyEdited(true); // Don't auto-generate for editing
        setDescription(material.description || '');
        setCategoryId(material.categoryId || '');
        setDefaultSupplier(material.defaultSupplier || '');
        setUnit(material.unit || '');
        setUnitSystem((material.unitSystem as 'metric' | 'imperial') || 'metric');
        setAverageCost(material.averageCost?.toString() || '');
        setDefaultPricePerUnit(material.defaultPricePerUnit?.toString() || '');
        setReorderPoint(material.reorderPoint?.toString() || '');
        setSuggestedOrderQuantity(material.suggestedOrderQuantity?.toString() || '');
        setLeadTimeDays(material.leadTimeDays?.toString() || '');
        setIsActive(material.isActive ?? true);
        setInitialQuantity('');
        setInitialCost('');
        setWarehouseId('');
      } else {
        setName('');
        setSku('');
        setSkuManuallyEdited(false);
        setDescription('');
        setCategoryId('');
        setDefaultSupplier('');
        setUnit('');
        setUnitSystem('imperial');
        setAverageCost('');
        setDefaultPricePerUnit('');
        setReorderPoint('');
        setSuggestedOrderQuantity('');
        setLeadTimeDays('');
        setIsActive(true);
        setInitialQuantity('');
        setInitialCost('');
        // Deliberately left empty: pre-selecting the default warehouse made
        // initial stock land somewhere the user never chose, which later reads
        // as "Out of Stock" wherever they actually expected it.
        setWarehouseId('');
      }
    }
  }, [isOpen, material, warehouses]);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    const baseData = {
      name: name.trim(),
      sku: sku.trim() || undefined,
      description: description.trim() || undefined,
      categoryId: categoryId || undefined,
      defaultSupplier: defaultSupplier.trim() || undefined,
      unit: unit || undefined,
      unitSystem: unitSystem || undefined,
      defaultPricePerUnit: defaultPricePerUnit ? parseFloat(defaultPricePerUnit) : undefined,
      reorderPoint: reorderPoint ? parseInt(reorderPoint, 10) : undefined,
      suggestedOrderQuantity: suggestedOrderQuantity ? parseInt(suggestedOrderQuantity, 10) : undefined,
      leadTimeDays: leadTimeDays ? parseInt(leadTimeDays, 10) : undefined,
      isActive,
    };

    if (isEditing && material) {
      const updateData: MaterialCatalogUpdateInput = {
        catalogId: material.catalogId,
        ...baseData,
      };
      await onSubmit(updateData);
    } else {
      const createData: MaterialCatalogCreateInput = {
        ...baseData,
        averageCost: averageCost ? parseFloat(averageCost) : undefined,
      };

      // Build initial stock data if quantity and warehouse are provided
      const qty = parseFloat(initialQuantity);
      const cost = parseFloat(initialCost);
      const hasInitialStock = qty > 0 && warehouseId;
      const stockData: InitialStockData | undefined = hasInitialStock
        ? { quantity: qty, unitCost: cost || 0, warehouseId }
        : undefined;

      await onSubmit(createData, stockData);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? tWarehouse.forms.editMaterialTitle : tWarehouse.forms.addMaterialTitle}
      confirmText={isEditing ? tWarehouse.forms.saveChanges : tWarehouse.forms.addMaterial}
      handleConfirm={handleSubmit}
      isLoading={isLoading}
      disableConfirm={!name.trim() || needsWarehouseForInitialStock}
      size="large"
    >
      <div className="flex flex-col gap-6 max-h-[70vh] overflow-y-auto pr-2">
        {/* Basic Information Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <InfoOutlinedIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            {tWarehouse.forms.basicInformation}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label={tWarehouse.forms.nameRequired} htmlFor="materialName">
              <Input
                id="materialName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={tWarehouse.forms.exampleMaterialName}
                autoFocus
              />
            </FormGroup>

            <FormGroup label={tWarehouse.forms.skuInternalCode} htmlFor="materialSku">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    id="materialSku"
                    value={sku}
                    onChange={handleSkuChange}
                    placeholder={
                      name.trim().length >= 2
                        ? tWarehouse.forms.autoGeneratingSku
                        : tWarehouse.forms.typeNameFirst
                    }
                  />
                </div>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={handleRegenerateSku}
                    className="flex items-center justify-center w-10 h-10 rounded-md border border-gray-300 text-gray-500 hover:text-indigo-600 hover:border-indigo-300 transition-colors"
                    title={tWarehouse.forms.regenerateSku}
                  >
                    <AutorenewIcon sx={{ fontSize: 18 }} />
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {skuManuallyEdited
                  ? tWarehouse.forms.customSkuHint
                  : tWarehouse.forms.autoGeneratedFromNameCategory}
              </p>
            </FormGroup>

            <FormGroup label={tCommon.category} htmlFor="materialCategory">
              <ReactSelect
                options={categoryOptions}
                value={categoryOptions.find((o) => o.value === categoryId) || null}
                onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setCategoryId(option.value as string);
                  } else {
                    setCategoryId('');
                  }
                }}
                placeholder={tWarehouse.forms.selectCategoryPlaceholder}
                isClearable
              />
            </FormGroup>

            <FormGroup label={tWarehouse.forms.defaultSupplier} htmlFor="materialSupplier">
              <Input
                id="materialSupplier"
                value={defaultSupplier}
                onChange={(e) => setDefaultSupplier(e.target.value)}
                placeholder={tWarehouse.forms.exampleSupplierName}
              />
            </FormGroup>

            <div className="md:col-span-2">
              <FormGroup label={tCommon.description} htmlFor="materialDescription">
                <textarea
                  id="materialDescription"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={tWarehouse.forms.additionalDetailsMaterial}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  rows={2}
                />
              </FormGroup>
            </div>
          </div>
        </div>

        {/* Units & Measurement Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <StraightenIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            {tWarehouse.forms.unitsAndMeasurement}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label={tWarehouse.forms.system} htmlFor="materialUnitSystem">
              <ReactSelect
                options={weightOptions}
                value={weightOptions.find((o) => o.value === unitSystem) || null}
                onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setUnitSystem(option.value as 'metric' | 'imperial');
                    setUnit(''); // Reset unit when system changes
                  }
                }}
                placeholder={tWarehouse.forms.metricImperialPlaceholder}
                isClearable={false}
              />
            </FormGroup>

            <FormGroup label={tWarehouse.forms.defaultUnit} htmlFor="materialUnit">
              <ReactSelect
                options={unitSystem === 'metric' ? metricWeightOptions : imperialWeightOptions}
                value={
                  (unitSystem === 'metric' ? metricWeightOptions : imperialWeightOptions)
                    .find((o) => o.value === unit) || null
                }
                onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setUnit(option.value as string);
                  } else {
                    setUnit('');
                  }
                }}
                placeholder={tWarehouse.forms.selectUnitPlaceholder}
                customComponent={GroupedSelect}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isClearable
              />
            </FormGroup>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AttachMoneyIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            {tWarehouse.forms.pricing}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label={tWarehouse.forms.defaultCostPerUnit} htmlFor="materialCost">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  id="materialCost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={averageCost}
                  onChange={(e) => setAverageCost(e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-md py-2 pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={isEditing}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {isEditing ? tWarehouse.forms.costCalculatedFromTransactions : tWarehouse.forms.yourPurchaseCost}
              </p>
            </FormGroup>

            <FormGroup label={tWarehouse.forms.defaultPricePerUnit} htmlFor="materialPrice">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  id="materialPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  value={defaultPricePerUnit}
                  onChange={(e) => setDefaultPricePerUnit(e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-md py-2 pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">{tWarehouse.forms.priceChargedToClients}</p>
            </FormGroup>
          </div>

          {/* Margin Preview */}
          <div className="mt-4 p-3 bg-white rounded-md border border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{tWarehouse.forms.estimatedMargin}</span>
              <span className={`font-medium ${margin && parseFloat(margin) > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                {margin ? `${margin}%` : tWarehouse.forms.marginNotCalculable}
              </span>
            </div>
          </div>
        </div>

        {/* Stock Alerts Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <NotificationsActiveIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            {tWarehouse.forms.stockAlerts}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormGroup label={tWarehouse.reorderPoint} htmlFor="reorderPoint">
              <Input
                id="reorderPoint"
                type="number"
                min="0"
                value={reorderPoint}
                onChange={(e) => setReorderPoint(e.target.value)}
                placeholder="10"
              />
              <p className="mt-1 text-xs text-gray-500">{tWarehouse.forms.alertBelowQuantity}</p>
            </FormGroup>

            <FormGroup label={tWarehouse.forms.suggestedOrderQty} htmlFor="reorderQuantity">
              <Input
                id="reorderQuantity"
                type="number"
                min="0"
                value={suggestedOrderQuantity}
                onChange={(e) => setSuggestedOrderQuantity(e.target.value)}
                placeholder="50"
              />
              <p className="mt-1 text-xs text-gray-500">{tWarehouse.forms.suggestedQtyHelp}</p>
            </FormGroup>

            <FormGroup label={tWarehouse.forms.leadTimeDays} htmlFor="leadTimeDays">
              <Input
                id="leadTimeDays"
                type="number"
                min="0"
                value={leadTimeDays}
                onChange={(e) => setLeadTimeDays(e.target.value)}
                placeholder="7"
              />
              <p className="mt-1 text-xs text-gray-500">{tWarehouse.forms.leadTimeHelp}</p>
            </FormGroup>
          </div>
        </div>

        {/* Initial Stock Section (Only for new materials) */}
        {!isEditing && warehouses.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <InventoryIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
              {tWarehouse.forms.initialStock}
              <span className="text-xs font-normal text-gray-500 ml-1">({tCommon.optional})</span>
            </h3>

            <div className="p-3 bg-blue-50 rounded-md border border-blue-200 mb-4">
              <div className="flex items-start gap-2">
                <InfoOutlinedIcon className="text-blue-500 mt-0.5" sx={{ fontSize: 16 }} />
                <p className="text-sm text-blue-700">
                  {tWarehouse.forms.initialStockHelp}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormGroup label={tWarehouse.forms.initialQuantity} htmlFor="initialQuantity">
                <Input
                  id="initialQuantity"
                  type="number"
                  min="0"
                  value={initialQuantity}
                  onChange={(e) => setInitialQuantity(e.target.value)}
                  placeholder="0"
                />
              </FormGroup>

              <FormGroup label={tWarehouse.unitCost} htmlFor="initialCost">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <input
                    id="initialCost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={initialCost}
                    onChange={(e) => setInitialCost(e.target.value)}
                    placeholder="0.00"
                    className="w-full border border-gray-300 rounded-md py-2 pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </FormGroup>

              <FormGroup
                label={
                  wantsInitialStock ? tWarehouse.warehouseRequired : tWarehouse.warehouseLabel
                }
                htmlFor="initialWarehouse"
                error={
                  needsWarehouseForInitialStock
                    ? tWarehouse.forms.chooseWarehouseForStock
                    : undefined
                }>
                <ReactSelect
                  options={warehouseOptions}
                  value={warehouseOptions.find((o) => o.value === warehouseId) || null}
                  onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                    if (option && !Array.isArray(option) && 'value' in option) {
                      setWarehouseId(option.value as string);
                    } else {
                      setWarehouseId('');
                    }
                  }}
                  placeholder={tWarehouse.selectWarehousePlaceholder}
                  error={needsWarehouseForInitialStock}
                  isClearable
                />
              </FormGroup>
            </div>

            {needsWarehouseForInitialStock && (
              <div className="flex items-start gap-2 p-3 mt-4 bg-amber-50 rounded-md border border-amber-200">
                <InfoOutlinedIcon className="text-amber-500 mt-0.5" sx={{ fontSize: 16 }} />
                <p className="text-sm text-amber-700">
                  {tWarehouse.forms.selectWarehouseHelp}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Status Toggle */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">{tWarehouse.forms.activeStatus}</h3>
              <p className="text-sm text-gray-500">{tWarehouse.forms.inactiveHelp}</p>
            </div>
            <button
              type="button"
              onClick={() => setIsActive(!isActive)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
                isActive ? 'bg-indigo-600' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={isActive}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isActive ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MaterialCatalogFormModal;
